import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { getPowerpartsCategories, listPowerparts } from "../lib/api.js";
import type { PowerpartsCategory, PowerpartsItem } from "../lib/types.js";
import { LoadingBlock } from "../ui/Spinner.js";
import { EmptyState } from "../ui/EmptyState.js";
import { Button } from "../ui/Button.js";
import { Badge } from "../ui/Badge.js";
import { PriceDisplay } from "../ui/PriceDisplay.js";
import { PackageIcon, HashIcon, ChevronRightIcon } from "../lib/icons.js";

/**
 * PowerParts / Garments / Technical Accessories / WP Aftermarket katalog.
 *
 * Data pochází z FTP ceníku (`article.productHierarchyL2`). Tyhle články
 * NEJSOU vázané na modely přes rozkresy — vlastní filtr + grid UI.
 */
export function PowerpartsPage(): JSX.Element {
  const [categories, setCategories] = useState<PowerpartsCategory[] | null>(null);
  const [selectedL2, setSelectedL2] = useState<string | null>(null);
  const [selectedL3, setSelectedL3] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [hasStock, setHasStock] = useState(false);
  /**
   * Default: skryté zrušené produkty (articleStatus = '80') — user vidí jen
   * aktuální sortiment. Přepínač níže umožní zobrazit i zrušené pro případ
   * ohledávání nabídky pro starší motorku.
   */
  const [showDiscontinued, setShowDiscontinued] = useState(false);

  const [items, setItems] = useState<PowerpartsItem[] | null>(null);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const LIMIT = 48;

  useEffect(() => {
    getPowerpartsCategories()
      .then((d) => {
        setCategories(d.categories);
        if (!selectedL2 && d.categories.length > 0) {
          setSelectedL2(d.categories[0]!.name);
        }
      })
      .catch(() => setCategories([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounce search
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setOffset(0);
  }, [selectedL2, selectedL3, debouncedSearch, hasStock, showDiscontinued]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listPowerparts({
      l2: selectedL2 ?? undefined,
      l3: selectedL3 ?? undefined,
      search: debouncedSearch || undefined,
      limit: LIMIT,
      offset,
      hasStock,
      includeDiscontinued: showDiscontinued,
    })
      .then((d) => {
        if (cancelled) return;
        setItems(d.items);
        setTotal(d.total);
      })
      .catch(() => !cancelled && setItems([]))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [selectedL2, selectedL3, debouncedSearch, hasStock, showDiscontinued, offset]);

  const l3Options = useMemo(() => {
    if (!categories || !selectedL2) return [];
    return categories.find((c) => c.name === selectedL2)?.l3Items ?? [];
  }, [categories, selectedL2]);

  if (!categories) return <LoadingBlock label="Načítám kategorie…" />;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
      <header className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight text-zinc-900">
          PowerParts · Garments · Accessories
        </h1>
        <p className="mt-1 text-sm text-zinc-500">
          Kompletní sortiment KTM/HQV/GasGas/WP mimo náhradní díly — oblečení,
          příslušenství a aftermarket komponenty.
        </p>
      </header>

      {/* Category row */}
      <div className="mb-4 flex gap-2 overflow-x-auto pb-1">
        {categories.map((c) => (
          <button
            key={c.name}
            type="button"
            onClick={() => {
              setSelectedL2(c.name);
              setSelectedL3(null);
            }}
            className={`px-4 py-2 rounded-lg text-sm whitespace-nowrap border transition-colors ${
              selectedL2 === c.name
                ? "bg-ktm-orange text-white border-ktm-orange"
                : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-300"
            }`}
          >
            <span className="font-medium">{translateCategory(c.name)}</span>
            <span className={`ml-2 text-xs ${selectedL2 === c.name ? "text-white/80" : "text-zinc-400"}`}>
              {c.count.toLocaleString("cs-CZ")}
            </span>
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="mb-6 flex items-center gap-3 flex-wrap">
        {l3Options.length > 0 && (
          <select
            value={selectedL3 ?? ""}
            onChange={(e) => setSelectedL3(e.target.value || null)}
            className="h-9 px-3 rounded-md border border-zinc-200 text-sm bg-white"
          >
            <option value="">Všechny značky</option>
            {l3Options.map((l3) => (
              <option key={l3.name} value={l3.name}>
                {l3.name} ({l3.count.toLocaleString("cs-CZ")})
              </option>
            ))}
          </select>
        )}
        <div className="relative">
          <HashIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Hledat číslo nebo název…"
            className="h-11 sm:h-9 pl-9 pr-3 rounded-md border border-zinc-200 text-sm focus:border-ktm-orange focus:ring-2 focus:ring-ktm-orange-100 focus:outline-none w-full sm:w-72"
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-zinc-700">
          <input
            type="checkbox"
            checked={hasStock}
            onChange={(e) => setHasStock(e.target.checked)}
          />
          Jen skladem ve Vlašimi
        </label>
        <label className="flex items-center gap-2 text-sm text-zinc-700">
          <input
            type="checkbox"
            checked={showDiscontinued}
            onChange={(e) => setShowDiscontinued(e.target.checked)}
          />
          Zobrazit i zrušené
        </label>
        <div className="ml-auto text-sm text-zinc-500">
          {total.toLocaleString("cs-CZ")} položek
        </div>
      </div>

      {/* Grid */}
      {loading && !items ? (
        <LoadingBlock label="Načítám…" />
      ) : !items || items.length === 0 ? (
        <EmptyState
          icon={<PackageIcon size={28} />}
          title="Žádné výsledky"
          description="Upravte filtr nebo hledání."
        />
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {items.map((it) => (
            <PowerpartsCard key={it.id} item={it} />
          ))}
        </div>
      )}

      {/* Pagination */}
      {total > LIMIT && (
        <div className="mt-6 flex items-center justify-between text-sm">
          <Button
            size="sm"
            variant="secondary"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - LIMIT))}
          >
            ← Předchozí
          </Button>
          <div className="text-xs text-zinc-500">
            {offset + 1}–{Math.min(offset + LIMIT, total)} z{" "}
            {total.toLocaleString("cs-CZ")}
          </div>
          <Button
            size="sm"
            variant="secondary"
            disabled={offset + LIMIT >= total}
            onClick={() => setOffset(offset + LIMIT)}
          >
            Další →
          </Button>
        </div>
      )}
    </div>
  );
}

function PowerpartsCard({ item }: { item: PowerpartsItem }): JSX.Element {
  const isDiscontinued = item.articleStatus === "80";
  const hasStock = item.physicalSellable > 0;

  return (
    <Link
      to={`/part/${encodeURIComponent(item.articleNumber)}`}
      className="group bg-white rounded-2xl border border-zinc-200 overflow-hidden hover:border-ktm-orange hover:shadow-md transition-all"
    >
      <div className="aspect-square bg-zinc-50 relative overflow-hidden">
        {item.imageUrl ? (
          <img
            src={item.imageUrl}
            alt={item.name ?? ""}
            className="w-full h-full object-contain p-4 group-hover:scale-[1.03] transition-transform"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-zinc-300">
            <PackageIcon size={40} />
          </div>
        )}
        {isDiscontinued && (
          <div className="absolute top-2 right-2">
            <Badge tone="red">Zrušeno</Badge>
          </div>
        )}
        {hasStock && (
          <div className="absolute bottom-2 left-2 inline-flex items-center gap-1 bg-white/95 backdrop-blur px-2 py-1 rounded-md border border-emerald-200 text-emerald-700 text-[11px] font-semibold">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
            {item.physicalSellable} ks Vlašim
          </div>
        )}
      </div>

      <div className="p-3">
        <div className="text-xs text-zinc-400 font-mono flex items-center gap-1.5">
          <span>{item.articleNumber}</span>
          {item.productHierarchyL3 && (
            <>
              <span className="text-zinc-300">·</span>
              <span>{item.productHierarchyL3}</span>
            </>
          )}
        </div>
        <div className="mt-1 text-sm font-medium text-zinc-900 line-clamp-2 group-hover:text-ktm-orange transition-colors min-h-[2.5rem]">
          {item.name ?? "Bez názvu"}
        </div>
        <div className="mt-2 flex items-center justify-between">
          <div>
            {item.price ? (
              <PriceDisplay price={item.price} size="sm" />
            ) : (
              <span className="text-xs text-zinc-400">Bez ceny</span>
            )}
          </div>
          <ChevronRightIcon size={14} className="text-zinc-300 group-hover:text-ktm-orange transition-colors" />
        </div>
      </div>
    </Link>
  );
}

function translateCategory(l2: string): string {
  const map: Record<string, string> = {
    Garments: "Oblečení",
    "Technical Accessories": "PowerParts (Accessories)",
    "WP Aftermarket": "WP Aftermarket",
    "Marketing Material": "Marketing",
    "Retail Systems": "Retail / Shop",
  };
  return map[l2] ?? l2;
}
